/**
 * Inventario local del taller — memoria para agentes (sin cloud).
 * Migrates legacy PROMETHEUS_OWNED_TOOLS + BOM inStock from projects.
 */

import { listProjects } from "@/lib/prometheusProjectsStore";

const INVENTORY_KEY = "prometheus_inventory_v1";
const LEGACY_TOOLS_KEY = "PROMETHEUS_OWNED_TOOLS";

export type InventoryKind = "tool" | "material" | "consumable" | "other";

export type InventoryItem = {
  id: string;
  name: string;
  kind: InventoryKind;
  qty: number;
  unit?: string;
  notes?: string;
  /** Linked engineering project id when sourced from BOM */
  projectId?: string;
  /** BOM line id when sourced from project */
  bomId?: string;
  source: "manual" | "chat" | "bom" | "legacy";
  updatedAt: string;
};

export type InventorySnapshot = {
  items: InventoryItem[];
  updatedAt: string;
};

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `inv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function safeParse(raw: string | null): InventoryItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // Legacy: string[]
      if (parsed.every((x) => typeof x === "string")) {
        return (parsed as string[]).map((name) => ({
          id: newId(),
          name,
          kind: "tool" as const,
          qty: 1,
          source: "legacy" as const,
          updatedAt: new Date().toISOString(),
        }));
      }
      return (parsed as InventoryItem[]).filter(
        (it) => it && typeof it.id === "string" && typeof it.name === "string"
      );
    }
    if (parsed && typeof parsed === "object" && Array.isArray((parsed as InventorySnapshot).items)) {
      return (parsed as InventorySnapshot).items;
    }
  } catch {
    /* ignore */
  }
  return [];
}

function readRaw(): InventoryItem[] {
  if (typeof localStorage === "undefined") return [];
  const primary = safeParse(localStorage.getItem(INVENTORY_KEY));
  if (primary.length > 0) return primary;
  return safeParse(localStorage.getItem(LEGACY_TOOLS_KEY));
}

function writeItems(items: InventoryItem[]) {
  if (typeof localStorage === "undefined") return;
  const snap: InventorySnapshot = {
    items,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(INVENTORY_KEY, JSON.stringify(snap));
  // Keep legacy key in sync for older chat heuristics
  const toolNames = items
    .filter((i) => i.kind === "tool" || i.kind === "other")
    .map((i) => i.name);
  localStorage.setItem(LEGACY_TOOLS_KEY, JSON.stringify(toolNames));
  window.dispatchEvent(new Event("prometheus-inventory-changed"));
}

/** Merge BOM inStock rows into inventory (idempotent by bomId). */
export function syncBomStockIntoInventory(): InventoryItem[] {
  const items = [...readRaw()];
  const byBom = new Map(items.filter((i) => i.bomId).map((i) => [i.bomId!, i]));
  let changed = false;

  for (const project of listProjects()) {
    const bom = (project as { bom?: Array<Record<string, unknown>> }).bom;
    if (!Array.isArray(bom)) continue;
    for (const row of bom) {
      if (!row || row.inStock !== true) continue;
      const bomId = typeof row.id === "string" ? row.id : null;
      const name = typeof row.name === "string" ? row.name : null;
      if (!name) continue;
      const qty = typeof row.quantity === "number" ? row.quantity : 1;
      const unit = typeof row.unit === "string" ? row.unit : undefined;
      if (bomId && byBom.has(bomId)) {
        const existing = byBom.get(bomId)!;
        if (existing.name !== name || existing.qty !== qty) {
          existing.name = name;
          existing.qty = qty;
          existing.unit = unit;
          existing.updatedAt = new Date().toISOString();
          changed = true;
        }
        continue;
      }
      const next: InventoryItem = {
        id: newId(),
        name,
        kind: "material",
        qty,
        unit,
        projectId: project.id,
        bomId: bomId || undefined,
        source: "bom",
        updatedAt: new Date().toISOString(),
      };
      items.push(next);
      if (bomId) byBom.set(bomId, next);
      changed = true;
    }
  }

  if (changed) writeItems(items);
  return items;
}

export function listInventory(): InventoryItem[] {
  syncBomStockIntoInventory();
  return readRaw().sort((a, b) => a.name.localeCompare(b.name, "es"));
}

export function addInventoryItem(input: {
  name: string;
  kind?: InventoryKind;
  qty?: number;
  unit?: string;
  notes?: string;
  source?: InventoryItem["source"];
}): InventoryItem | null {
  const name = input.name.trim();
  if (name.length < 2) return null;
  const items = readRaw();
  const existing = items.find(
    (i) => i.name.toLowerCase() === name.toLowerCase() && !i.bomId
  );
  if (existing) {
    existing.qty = (existing.qty || 1) + (input.qty ?? 1);
    existing.updatedAt = new Date().toISOString();
    if (input.notes) existing.notes = input.notes;
    writeItems(items);
    return existing;
  }
  const item: InventoryItem = {
    id: newId(),
    name,
    kind: input.kind || "tool",
    qty: input.qty ?? 1,
    unit: input.unit,
    notes: input.notes,
    source: input.source || "manual",
    updatedAt: new Date().toISOString(),
  };
  writeItems([...items, item]);
  return item;
}

export function updateInventoryItem(
  id: string,
  patch: Partial<Pick<InventoryItem, "name" | "kind" | "qty" | "unit" | "notes">>
): InventoryItem | null {
  const items = readRaw();
  const idx = items.findIndex((i) => i.id === id);
  if (idx < 0) return null;
  items[idx] = {
    ...items[idx],
    ...patch,
    id: items[idx].id,
    updatedAt: new Date().toISOString(),
  };
  writeItems(items);
  return items[idx];
}

export function removeInventoryItem(id: string): boolean {
  const items = readRaw();
  const next = items.filter((i) => i.id !== id);
  if (next.length === items.length) return false;
  writeItems(next);
  return true;
}

/** One-line / block for agent system context. */
export function buildInventoryContextBlock(): string {
  const items = listInventory();
  if (items.length === 0) {
    return `[INVENTARIO DEL TALLER]: Vacío. Pregunta qué herramientas/materiales ya tiene Angel en PR antes de asumir compras.`;
  }
  const tools = items.filter((i) => i.kind === "tool");
  const mats = items.filter((i) => i.kind === "material" || i.kind === "consumable");
  const other = items.filter((i) => i.kind === "other");
  const fmt = (list: InventoryItem[]) =>
    list
      .map((i) => `${i.name}${i.qty !== 1 ? ` ×${i.qty}` : ""}${i.unit ? ` ${i.unit}` : ""}`)
      .join("; ");

  return `[INVENTARIO REAL DEL TALLER — PRIORIZA ESTO]:
- Herramientas / equipos: ${tools.length ? fmt(tools) : "ninguna registrada"}.
- Materiales / consumibles en stock: ${mats.length ? fmt(mats) : "ninguno registrado"}.
- Otros: ${other.length ? fmt(other) : "—"}.
DIRECTIVA: No pidas comprar lo que ya está en inventario. Si falta algo, sugiere tiendas PR (Home Depot, National Lumber, Ace, Mouser/USPS) con USD.`;
}

export function inventoryCounts(): { total: number; tools: number; materials: number } {
  const items = listInventory();
  return {
    total: items.length,
    tools: items.filter((i) => i.kind === "tool").length,
    materials: items.filter((i) => i.kind === "material" || i.kind === "consumable").length,
  };
}
